import React from "react";
import { Star } from "lucide-react";
import ProductCard from "./ProductCard";

// Customer Favorites (Aug 2026) — the first tab in the category bar.
// Reuses the SAME `ProductCard`/add-to-cart machinery every other
// section uses (never a second, parallel add-to-cart implementation) —
// the only special handling here is the three states a normal category
// section never needs: not-a-member, still-loading, and a saved
// favorite whose product could not be resolved (deleted, never
// re-imported) — shown as a safe, minimal "no longer available" card
// rather than any reconstructed/stale product data.
export function FavoritesSection({
  catName, sectionRef, member, verified = true, loaded, items, currency, itemsInCart,
  onAdd, onSetQty, onRemove, onInfo, onToggleFavorite, onRemoveFavorite, onRequireMembership, onRequireVerification,
  // Customer-selectable spice (Sep 2026, restored) — a Favorites card is
  // the same ProductCard, so it offers the same choice, and resolves
  // its stepper against the same `(product_id, spice_level)` index.
  spiceEnabled = true, cartLines,
}) {
  return (
    <section data-cat={catName} ref={sectionRef} className="px-3 sm:px-5 pt-4">
      <div
        className="bg-emerald-900/90 text-emerald-50 rounded-t-md px-4 py-2 flex items-center justify-between"
        data-testid="retail-section-favorites"
      >
        <h2 className="font-serif text-lg flex items-center gap-1.5">
          <Star className="w-4 h-4 fill-current" /> Favorites
        </h2>
        {verified && member && loaded && (
          <span className="text-xs text-emerald-200/70">{items.length} item{items.length === 1 ? "" : "s"}</span>
        )}
      </div>
      <div className="bg-[#e8efd0] rounded-b-md p-3 sm:p-4">
        {!verified ? (
          // Walk-in customer recognition/verification correction (Aug
          // 2026) — a recognized-but-unverified (or never-recognized)
          // customer must never see "Join Membership" — that implies
          // they've already been checked against membership status,
          // which requires OTP verification first. This is a genuinely
          // distinct state from "verified non-member" below.
          <div className="grid place-items-center py-10 text-center" data-testid="retail-favorites-unverified">
            <Star className="w-8 h-8 text-emerald-700/40 mb-2" />
            <div className="text-sm font-medium text-emerald-900">Verify to view your Favorites</div>
            <p className="text-xs text-emerald-800/70 mt-1 max-w-xs">
              Verify your phone number to see your saved favorites and start saving new ones.
            </p>
            <button
              type="button"
              onClick={onRequireVerification || onRequireMembership}
              data-testid="retail-favorites-verify-btn"
              className="mt-3 text-xs font-bold px-3 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600"
            >
              Verify
            </button>
          </div>
        ) : !member ? (
          <div className="grid place-items-center py-10 text-center" data-testid="retail-favorites-locked">
            <Star className="w-8 h-8 text-emerald-700/40 mb-2" />
            <div className="text-sm font-medium text-emerald-900">Favorites are for members</div>
            <p className="text-xs text-emerald-800/70 mt-1 max-w-xs">
              Join our membership program to save your favorite items and find them here every visit.
            </p>
            <button
              type="button"
              onClick={onRequireMembership}
              data-testid="retail-favorites-join-btn"
              className="mt-3 text-xs font-bold px-3 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600"
            >
              Join Membership
            </button>
          </div>
        ) : !loaded ? (
          <div className="grid place-items-center py-10 text-emerald-800/50 text-sm" data-testid="retail-favorites-loading">
            Loading your favorites…
          </div>
        ) : items.length === 0 ? (
          <div className="grid place-items-center py-10 text-center" data-testid="retail-favorites-empty">
            <Star className="w-8 h-8 text-emerald-700/30 mb-2" />
            <div className="text-sm font-medium text-emerald-900">No favorites yet</div>
            <p className="text-xs text-emerald-800/70 mt-1">
              Tap the star on any item to save it here.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {items.map((item) =>
              item.product ? (
                <ProductCard
                  key={item.product_id}
                  product={item.product}
                  currency={currency}
                  inCart={itemsInCart.get(item.product_id)}
                  cartLines={cartLines}
                  onAdd={(spiceLevel) => onAdd(item.product_id, spiceLevel)}
                  spiceEnabled={spiceEnabled}
                  onInc={(it) => onSetQty(it.item_id || it.id, (it.quantity || 0) + 1)}
                  onDec={(it) => {
                    const next = (it.quantity || 0) - 1;
                    if (next <= 0) return onRemove(it.item_id || it.id);
                    return onSetQty(it.item_id || it.id, next);
                  }}
                  onInfo={onInfo}
                  favoriteState="filled"
                  onToggleFavorite={onToggleFavorite}
                />
              ) : (
                <div
                  key={item.favorite_id}
                  data-testid={`retail-favorite-unavailable-${item.favorite_id}`}
                  className="bg-[#fdfbf0] text-stone-500 rounded-md border border-stone-300 border-dashed flex flex-col items-center justify-center gap-2 p-4 text-center"
                >
                  <span className="text-xs">This saved item is no longer available.</span>
                  <button
                    type="button"
                    onClick={() => onRemoveFavorite(item.product_id)}
                    data-testid={`retail-favorite-remove-${item.favorite_id}`}
                    className="text-[11px] font-semibold px-2 py-1 rounded bg-stone-200 hover:bg-stone-300 text-stone-700"
                  >
                    Remove
                  </button>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default FavoritesSection;
